import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    
    // Add RTL support for Arabic
    if (languageCode === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', languageCode);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-1.5 md:space-x-2 p-1.5 sm:p-2 md:p-2.5 lg:p-3 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-105 bg-gradient-to-r from-teal-500/10 to-emerald-600/10 backdrop-blur-sm border border-white/20 hover:from-teal-500/20 hover:to-emerald-600/20"
      >
        {/* Flag on mobile, Globe on larger screens */}
        <span className="text-lg sm:hidden flex-shrink-0">{currentLanguage.flag}</span>
        <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-teal-600 flex-shrink-0 hidden sm:block" />
        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 hidden sm:inline-flex items-center gap-1">
          <span>{currentLanguage.flag}</span>
          <span className="hidden md:inline">{currentLanguage.name}</span>
        </span>
        <ChevronDown className={`h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} flex-shrink-0 hidden sm:block`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-40 sm:w-48 rounded-xl sm:rounded-2xl shadow-2xl py-1 sm:py-2 z-[9999] backdrop-blur-sm bg-white/90 border border-white/20">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={`w-full text-left flex items-center px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-105 ${
                i18n.language === language.code
                  ? 'bg-gradient-to-r from-teal-500/20 to-emerald-600/20 text-teal-600'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <span className="text-base sm:text-lg mr-2 sm:mr-3 flex-shrink-0">{language.flag}</span>
              <span className="truncate">{language.name}</span>
              {i18n.language === language.code && (
                <div className="ml-auto w-1.5 h-1.5 sm:w-2 sm:h-2 bg-teal-500 rounded-full flex-shrink-0"></div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSwitcher;
